import { resolveFormSchemaIdFromPageEntry } from '@/common/utils/resolvePageIdentity'
import { resolveBoundDocumentIdentityFromPage } from '@/common/utils/documentTransferIdentity'
import { resolveResponseSchemaFormId } from '@/common/utils/resolvePageStoreEntry'

function normalizePositiveSchemaId(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return String(value)
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed === '0') return ''
    const parsed = parseInt(trimmed, 10)
    if (Number.isFinite(parsed) && parsed > 0) return String(parsed)
  }
  return ''
}

function responseHasRenderableForm(form: unknown): boolean {
  if (!form || typeof form !== 'object') return false
  const f = form as Record<string, unknown>
  const topHasLayout =
    (Array.isArray(f.main) && f.main.length > 0) ||
    (Array.isArray(f.header) && f.header.length > 0) ||
    (Array.isArray(f.footer) && f.footer.length > 0)
  if (topHasLayout) return true
  const inner = f.form as Record<string, unknown> | undefined
  if (inner && typeof inner === 'object') {
    return (
      (Array.isArray(inner.main) && (inner.main as unknown[]).length > 0) ||
      (Array.isArray(inner.header) && (inner.header as unknown[]).length > 0) ||
      (Array.isArray(inner.footer) && (inner.footer as unknown[]).length > 0) ||
      inner.panels != null
    )
  }
  return false
}

/** schema id из layout в response.form (может не совпадать с status/stack). */
export function resolveSameWindowLayoutFormId(
  response: Record<string, unknown> | null | undefined,
): string {
  if (!response || typeof response !== 'object') return ''
  const formRoot = response.form as Record<string, unknown> | undefined
  const formInner = formRoot?.form as Record<string, unknown> | undefined
  const formAttrs = (formInner?._attributes ?? formRoot?._attributes) as
    | Record<string, unknown>
    | undefined
  return (
    normalizePositiveSchemaId(formAttrs?.id) ||
    normalizePositiveSchemaId(formAttrs?.fid) ||
    ''
  )
}

export function resolveSameWindowAuthoritativeFormId(
  response: Record<string, unknown> | null | undefined,
): string {
  if (!response || typeof response !== 'object') return ''
  const jsResult = response.jsResult as Record<string, unknown> | undefined
  const stack = Array.isArray(response.stack) ? response.stack : []
  const fromMeta =
    normalizePositiveSchemaId(jsResult?.form_id) ||
    normalizePositiveSchemaId(stack[0]) ||
    normalizePositiveSchemaId(response.status)
  const fromLayout = resolveSameWindowLayoutFormId(response)
  if (fromMeta && fromLayout && fromMeta !== fromLayout) {
    return fromMeta
  }
  return resolveResponseSchemaFormId(response as Record<string, any>) || fromLayout
}

/** @deprecated use resolveSameWindowAuthoritativeFormId */
export function resolveSameWindowOpenedFormId(
  response: Record<string, unknown> | null | undefined,
): string {
  return resolveSameWindowAuthoritativeFormId(response)
}

export function sameWindowResponseFormLayoutMismatch(
  response: Record<string, unknown> | null | undefined,
): boolean {
  if (!response || !responseHasRenderableForm(response.form)) return false
  const authoritative = resolveSameWindowAuthoritativeFormId(response)
  const layoutId = resolveSameWindowLayoutFormId(response)
  return Boolean(authoritative && layoutId && authoritative !== layoutId)
}

export function sameWindowSchemaUnchangedOnPage(
  currentPageData: Record<string, unknown> | null | undefined,
  response: Record<string, unknown> | null | undefined,
  slotId: string,
): boolean {
  if (!currentPageData || !response) return false
  const currentSchemaId = String(
    resolveFormSchemaIdFromPageEntry(currentPageData, slotId) || '',
  )
  const nextSchemaId = resolveSameWindowAuthoritativeFormId(response)
  return (
    currentSchemaId !== '' &&
    nextSchemaId !== '' &&
    currentSchemaId === nextSchemaId
  )
}

export function sameWindowResponseNeedsFullFormRefetch(
  currentPageData: Record<string, unknown> | null | undefined,
  response: Record<string, unknown> | null | undefined,
  slotId: string,
): boolean {
  if (!response) return false
  if (sameWindowResponseFormLayoutMismatch(response)) return true
  if (response.form != null && responseHasRenderableForm(response.form)) return false

  const openedId = resolveSameWindowAuthoritativeFormId(response)
  if (!openedId) return false
  if (!currentPageData) return true

  const currentSchemaId = String(
    resolveFormSchemaIdFromPageEntry(currentPageData, slotId) || '',
  )
  if (!currentSchemaId) return true
  return openedId !== currentSchemaId
}

export function resolveDialogSlotRegistryFormId(
  pageData: Record<string, unknown> | null | undefined,
  dialogSlotId: string,
  dialogStoreFid?: string | null,
): string {
  const slot = String(dialogSlotId ?? '').trim()
  const fromPage = resolveFormSchemaIdFromPageEntry(pageData, slot)
  const fromPageStr = fromPage > 0 ? String(fromPage) : ''
  const explicit = String(dialogStoreFid ?? '').trim()

  if (fromPageStr && explicit && fromPageStr !== explicit && fromPageStr !== slot) {
    return fromPageStr
  }
  if (fromPageStr) return fromPageStr
  if (explicit) return explicit
  return slot
}

export function resolveDialogSlotSchemaRemountKey(
  pageData: Record<string, unknown> | null | undefined,
  dialogSlotId: string,
  remountGen = 0,
): string {
  const slot = String(dialogSlotId ?? '').trim()
  const schemaId = resolveFormSchemaIdFromPageEntry(pageData, slot)
  const schemaPart = schemaId > 0 ? String(schemaId) : '0'
  const sessionPart =
    pageData?.sessionId != null && String(pageData.sessionId).trim() !== ''
      ? String(pageData.sessionId).trim()
      : ''
  const bound = resolveBoundDocumentIdentityFromPage(pageData)
  const docPart = bound?._id != null ? String(bound._id) : ''
  const instancePart = sessionPart || docPart || '0'
  return `${slot}:${schemaPart}:${instancePart}:${remountGen}`
}
